import React, { useMemo, useState } from "react";
import { Helmet } from "react-helmet-async";
import "./FabInfo.css";

function normalizeFaqItems(faqItems = []) {
  if (!Array.isArray(faqItems)) return [];

  return faqItems
    .map((item) => ({
      question: item?.question || "",
      answer_type: item?.answer_type || "text",
      answer_text: item?.answer_text || "",
      answer_lines: Array.isArray(item?.answer_lines) ? item.answer_lines.filter(Boolean) : [],
      answer_items: Array.isArray(item?.answer_items) ? item.answer_items : [],
      link_label: item?.link_label || "",
      link_url: item?.link_url || "",
    }))
    .filter((item) => item.question);
}

function normalizeHref(url = "") {
  const trimmed = url.trim();
  if (!trimmed) return "";

  if (
    trimmed.startsWith("http://") ||
    trimmed.startsWith("https://") ||
    trimmed.startsWith("/") ||
    trimmed.startsWith("mailto:") ||
    trimmed.startsWith("tel:")
  ) {
    return trimmed;
  }

  return `https://${trimmed}`;
}

function getLinkProps(url = "") {
  const normalized = normalizeHref(url);

  if (normalized.startsWith("http://") || normalized.startsWith("https://")) {
    return {
      target: "_blank",
      rel: "noopener noreferrer",
    };
  }

  return {};
}

function renderFaqAnswer(item) {
  const {
    answer_type = "text",
    answer_text = "",
    answer_lines = [],
    answer_items = [],
    link_label = "",
    link_url = "",
  } = item;

  if (answer_type === "ordered_list_with_links") {
    return (
      <ol>
        {answer_items.map((entry, index) => (
          <li key={index}>
            {entry?.text || ""}
            {entry?.link_label && entry?.link_url ? (
              <>
                {" "}
                <a href={normalizeHref(entry.link_url)} {...getLinkProps(entry.link_url)}>
                  {entry.link_label}
                </a>
              </>
            ) : null}
          </li>
        ))}
      </ol>
    );
  }

  if (answer_type === "ordered_list") {
    return (
      <ol>
        {answer_lines.map((line, index) => (
          <li key={index}>{line}</li>
        ))}
      </ol>
    );
  }

  if (answer_type === "list") {
    return (
      <ul>
        {answer_lines.map((line, index) => (
          <li key={index}>{line}</li>
        ))}
      </ul>
    );
  }

  if (answer_type === "link_text") {
    return (
      <p>
        {answer_text}{" "}
        {link_label && link_url ? (
          <a href={normalizeHref(link_url)} {...getLinkProps(link_url)}>
            {link_label}
          </a>
        ) : null}
      </p>
    );
  }

  return <p>{answer_text}</p>;
}

function FabInfo({ organization, page, settings }) {
  const [openIndex, setOpenIndex] = useState(null);

  const content = page?.content || {};
  const themeMode = settings?.theme_mode === "light" ? "light" : "dark";

  const introTitle = content.intro_title || "What is FAB";
  const introText =
    content.intro_text ||
    "Fast Action Basketball (FAB) is a nonprofit 501(c)4 adult basketball league operating in multiple locations in Southern California. Our league offers more than just a venue for basketball; it fosters an inclusive environment where camaraderie, spirited competition, and community thrive.";

  const faqItems = useMemo(() => normalizeFaqItems(content.faq_items), [content.faq_items]);

  const bannerOneTitle = content.banner_one_title || "It's not about the competition...";
  const bannerOneImageUrl = content.banner_one_image_url || "/leagueInfo1.jpg";

  const bannerTwoTitle = content.banner_two_title || "... it's about conquering yourself.";
  const bannerTwoImageUrl = content.banner_two_image_url || "/leagueInfo2.jpg";
  const orgName = organization?.name || "SportingHip";
  const seoTitle = `League Info | ${orgName}`;
  const seoDescription = introText || `Learn more about ${orgName}.`;
  return (
      <>
      <Helmet>
        <title>{seoTitle}</title>
        <meta name="description" content={seoDescription} />
        <meta property="og:title" content={seoTitle} />
        <meta property="og:description" content={seoDescription} />
        <meta property="og:site_name" content={orgName} />
        <link rel="canonical" href={window.location.origin + window.location.pathname} />
      </Helmet>
    <div className={`fab-info ${themeMode}`}>
      <div className="fab-info-main">
        <section className="fab-info-intro section-wrapper">
          <h1>{introTitle}</h1>
          <p>{introText}</p>
        </section>

        <section className="fab-info-faq section-wrapper">
          <h2>FAQs</h2>

          {faqItems.map((faq, index) => (
            <div
              key={index}
              className={`fab-info-faq-item ${openIndex === index ? "open" : ""}`}
            >
              <button
                type="button"
                className="fab-info-faq-question"
                onClick={() => setOpenIndex(openIndex === index ? null : index)}
              >
                {faq.question}
              </button>

              {openIndex === index && (
                <div className="fab-info-faq-answer">
                  {renderFaqAnswer(faq)}
                </div>
              )}
            </div>
          ))}
        </section>
      </div>

      <section
        className="fab-info-banner"
        style={{ backgroundImage: `url(${bannerOneImageUrl})` }}
      >
        <div className="fab-info-banner-overlay" />
        <div className="fab-info-banner-content section-wrapper">
          <h2>{bannerOneTitle}</h2>
        </div>
      </section>

      <section
        className="fab-info-banner"
        style={{ backgroundImage: `url(${bannerTwoImageUrl})` }}
      >
        <div className="fab-info-banner-overlay" />
        <div className="fab-info-banner-content section-wrapper">
          <h2>{bannerTwoTitle}</h2>
        </div>
      </section>
    </div>
    </>
  );
}

export default FabInfo;